// lib/myBooking.ts — the signed-in applicant's own booking and profile (GET /my-booking, GET /my-profile), the same
// endpoints the phone app reads (mobile/src/lib/booking.ts). Signed in is enough: the server answers only about the
// caller's own records. Only the fields the website's landing shows are typed here.
import { api } from './api';

export interface BookingStep {
  key: 'holding' | 'referencing' | 'sign' | 'money';
  label: string; done: boolean; lockedBy: string | null; pending: string | null;
}

export interface MyBooking {
  reservationId: string;
  /** /apply/<token>/sign — the booking's own web page is the same path without /sign. */
  signPath: string;
  status: 'applying' | 'with_lotus' | 'moved_in';
  reviewStatus: 'SUBMITTED' | 'QUALIFIED' | 'APPROVED' | 'REJECTED'; rejectionReason: string | null;
  property: string; flat: string; floor: number | null; bedrooms: number; unitType: string; areaSqft: number | null;
  rentMinor: number; currency: string; term: string; startDate: string | null;
  applicant: { name: string; email: string | null; phone: string | null; confirmedAt: string | null };
  agent: { name: string } | null;
  steps: BookingStep[]; done: number; total: number;
  tenancy: { reference: string; status: string } | null;
  /** The whole application form as last saved. */
  application: Record<string, any> | null;
  /** Lotus Living's "Ask to refill" waiting for an answer (cleared when the form is saved again). */
  refill: { fields: string[]; labels: string[]; note: string | null; askedAt: string; askedBy: string; channel: 'EMAIL' | 'SMS' } | null;
}

export interface MyProfile {
  account: { firstName: string | null; lastName: string | null; email: string; phone: string | null };
  screening: { status: string; maxRentMinor: number | null; currency: string };
}

/** One of the person's viewings (GET /app/my-viewings — docs/plans/viewing-first-onboarding.md, phase 4). */
export interface MyViewing {
  id: string; leadId: string; scheduledAt: string; durationMinutes: number;
  status: 'BOOKED' | 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED'; outcome: string | null;
  host: { firstName: string } | null;
  listing: { id: string; slug: string; headline: string | null; status: string; coverPhotoUrl: string | null; coverThumbUrl: string | null };
  unit: { id: string; reference: string; name: string | null; bedrooms: number; unitType: string };
  property: { id: string; name: string; city: string | null };
  homeId: string | null;
  /** Completed (not a no-show), the flat still advertised and free, no application of theirs on it. */
  canApply: boolean; applyBlockedBy: string | null; applicationId: string | null;
  /** The application form's prefill. */
  applicant: { firstName: string; lastName: string; email: string | null; mobile: string | null };
  rentMinor: number; currency: string; availableFrom: string | null;
}

export const myBookingApi = {
  viewings: async () => (await api.get<MyViewing[]>('/app/my-viewings')).data,
  /** Apply for the flat of a completed viewing (POST /app/applications with its lead); answers with the booking. */
  applyFromViewing: async (body: { leadId: string; listingId: string; form: Record<string, unknown>; documents: unknown[] }) =>
    (await api.post<{ reservationId: string; booking: MyBooking | null }>('/app/applications', { ...body, channel: 'WEBSITE' })).data,
  booking: async () => (await api.get<MyBooking | null>('/my-booking')).data,
  profile: async () => (await api.get<MyProfile>('/my-profile')).data,
  /** Save the whole application form again (POST /my-booking/form; answers an "Ask to refill"). */
  saveForm: async (form: Record<string, unknown>) => (await api.post<MyBooking>('/my-booking/form', { form })).data,
};

/** The booking's own page on the website (/apply/<token>): every step, uploads, the agreement to sign. */
export const bookingPagePath = (b: MyBooking) => b.signPath.replace(/\/sign$/, '');

/** What the booking is waiting on, in one line (the app's BookingCard nextLine). */
export function nextLine(b: MyBooking): string {
  if (b.reviewStatus === 'REJECTED') return 'Your application was not successful.';
  if (b.status === 'with_lotus') return 'Everything is in. Lotus Living is getting the home ready.';
  if (b.status === 'moved_in') return 'You have moved in.';
  if (b.reviewStatus !== 'APPROVED') return 'Lotus Living is reviewing your application.';
  const s = b.steps.find((x) => !x.done);
  if (!s) return 'Everything is done.';
  return s.pending ? `${s.label} — ${s.pending.toLowerCase()}` : `Next: ${s.label.charAt(0).toLowerCase()}${s.label.slice(1)}`;
}

/** A viewing's status in words, with the badge tone. */
export function viewingStatus(v: Pick<MyViewing, 'status' | 'scheduledAt'>): [string, 'good' | 'warning' | 'critical' | 'neutral'] {
  if (v.status === 'COMPLETED') return ['Viewed', 'good'];
  if (v.status === 'NO_SHOW') return ['Missed', 'critical'];
  if (v.status === 'CANCELLED') return ['Cancelled', 'neutral'];
  if (v.status === 'CONFIRMED') return ['Confirmed', 'good'];
  return [new Date(v.scheduledAt).getTime() < Date.now() ? 'Waiting for Lotus Living' : 'Booked', 'warning'];
}

/** "Tue 4 Nov, 10:30" in London time. */
export function viewingWhen(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
  return `${day}, ${time}`;
}

export const bedsLabel = (beds: number, unitType: string) => (unitType === 'STUDIO' || beds === 0 ? 'Studio' : `${beds} bed${beds === 1 ? '' : 's'}`);
